import { DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { Button, Col, Dropdown, Flex, Modal, Row, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import FormItem from "antd/es/form/FormItem";
import TableAntd from "components/elements/Table";
import { FormInput, FormSelect } from "components/form/FormProvide";
import FormAntd from "components/form/FormProvide/FormAntd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const WelfareAddEmployeeModal = ({
    isEmployeeModalOpen = false,
    setIsEmployeeModalOpen = () => { },
    // pagination = {},
    employeeOptions = [],
    selectedEmployees = [],
    initialEmployees = [],
    onSave = () => { }
}) => {
    const { t, i18n } = useTranslation();
    const [form] = useForm();
    const [tempEmployees, setTempEmployees] = useState([]);
    const [searchTxt, setSearchTxt] = useState("");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    useEffect(() => {
        if (isEmployeeModalOpen) {
            setTempEmployees(selectedEmployees);
            setPagination(prev => ({ ...prev, total: selectedEmployees.length }));
        }
    }, [isEmployeeModalOpen, selectedEmployees]);

    const onAddEmployee = (employeeIds) => {
        const newEmployees = employeeOptions?.filter(emp =>
            employeeIds.includes(emp.value) &&
            !tempEmployees.some(te => te.id === emp.value)
        ).map(emp => ({
            id: emp.value,
            fullname: emp.fullname || emp.label,
            fullnameEn: emp.fullnameEn || emp.label,
            employeeNo: emp.employeeNo || "",
        }));

        const updated = [...tempEmployees, ...newEmployees];
        setTempEmployees(updated);
        setPagination(prev => ({ ...prev, total: updated.length }));
        form.setFieldValue("employeeList", []);
    };

    const onRemoveEmployee = (record) => {
        const updated = tempEmployees.filter(emp => emp.id !== record.id);
        setTempEmployees(updated);
        setPagination(prev => ({ ...prev, total: updated.length }));
    };

    const onFilterEmployeeBySearch = tempEmployees.filter(emp => {
        if (!searchTxt) return true;
        const searchLower = searchTxt.toLowerCase();
        return (
            emp.employeeNo?.toLowerCase().includes(searchLower) ||
            emp.fullname?.toLowerCase().includes(searchLower) ||
            emp.fullnameEn?.toLowerCase().includes(searchLower)
        );
    });

    const onPageEmployeeChange = (page, pageSize) => {
        setPagination(prev => ({ ...prev, current: page, pageSize }));
    };

    const handleSave = () => {
        onSave(tempEmployees);
        form.resetFields();
        setSearchTxt("");
    };

    const handleCancel = () => {
        setIsEmployeeModalOpen(false);
        setTempEmployees(selectedEmployees);
        form.resetFields();
        setSearchTxt("");
    };

    const columns = [
        {
            title: <div className="text-table" style={{ textAlign: "center" }}>{t('common.index')}</div>,
            dataIndex: "index",
            key: "index",
            render: (_, record, idx) => {
                return <span
                    style={{
                        color: "#42526D",
                        backgroundColor: "#F5F6F7",
                        borderRadius: "50%",
                        display: "inline-flex",
                        justifyContent: "center",
                        alignItems: "center",
                        margin: "auto",
                        padding: "8px",
                        minWidth: "30px",
                        height: "30px",
                    }}
                >
                    {/* {idx + 1} */}
                    {((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 10) + idx + 1}
                </span>
            },
            width: 90,
            align: "center"
        },
        {
            title: <div className="text-table">{t('employee.label.employee_no')}</div>,
            dataIndex: "employeeNo",
            key: "employeeNo",
        },
        {
            title: <div className="text-table">{t('employee.label.name')}</div>,
            dataIndex: "fullname",
            key: "fullname",
            render: (_, record) => {
                return i18n.language === 'en' ? record?.fullnameEn : record?.fullname
            }
        },
        {
            title: "",
            key: "action",
            align: "center",
            width: 90,
            render: (_, record) => (
                <Space size="middle">
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: "1",
                                    onClick: () => onRemoveEmployee(record),
                                    label: `${t("action.delete")}`,
                                    icon: (
                                        <DeleteOutlined style={{ fontSize: "18px", color: "gray" }} />
                                    ),
                                },
                            ],
                        }}
                    >
                        <MoreOutlined style={{ fontSize: "25px", color: "gray" }} />
                    </Dropdown>
                </Space>
            ),
        }
    ]

    return (
        <Modal
            open={isEmployeeModalOpen}
            closable={false}
            maskClosable={false}
            footer={null}
            width={620}
        >
            <Space size={24} direction="vertical" style={{ width: "100%" }}>
                <Flex vertical>
                    <h3>{t("welfare.label.add_employee_list")}</h3>
                    <span style={{ color: "#667085" }}>{t("welfare.label.add_employee_list_desc")}</span>
                </Flex>

                <FormAntd
                    form={form}
                    onFinish={handleSave}
                >
                    <FormItem shouldUpdate>
                        {({ getFieldsValue, getFieldValue, setFieldValue, resetFields }) => {
                            return (
                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                    <Row>
                                        <Col span={19}>
                                            <Flex vertical>
                                                <FormSelect
                                                    name="employeeList"
                                                    label={t("welfare.label.add_employee_list")}
                                                    placeholder={`${t("employee.label.name")} / ${t("employee.label.employee_id")}`}
                                                    size="large"
                                                    mode="multiple"
                                                    options={employeeOptions}
                                                />
                                            </Flex>
                                        </Col>
                                        <Col span={5} style={{ alignContent: "end", textAlignLast: "end" }}>
                                            <Button
                                                onClick={() => {
                                                    onAddEmployee(getFieldValue("employeeList"));
                                                }}
                                                disabled={getFieldValue("employeeList") == null || getFieldValue("employeeList")?.length == 0}
                                                size="large"
                                                type="primary"
                                                style={{ borderRadius: "8px", backgroundColor: (getFieldValue("employeeList") == null || getFieldValue("employeeList")?.length == 0) ? "" : "var(--button-color)" }}
                                            >
                                                {t("employee.label.add")}
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Row style={{ gap: "6px" }}>
                                        <Col span={19}>
                                            <FormInput
                                                name="searchTxt"
                                                label={t("common.search_name_empId")}
                                                placeholder={t("common.fill_in")}
                                                size="large"
                                                allowClear={true}
                                            />
                                        </Col>
                                        <Col span={4} style={{ alignContent: "end" }}>
                                            <Button
                                                type="default"
                                                style={{ borderRadius: "8px", height: "40px", color: "#565656", fontWeight: "bold" }}
                                                onClick={() => setSearchTxt(getFieldValue("searchTxt") || "")}
                                            >
                                                {t("common.search")}
                                            </Button>
                                        </Col>
                                    </Row>
                                    <TableAntd
                                        columns={columns}
                                        dataSource={onFilterEmployeeBySearch}
                                        rowKey={(record) => record?.id}
                                        noPage={true}
                                        onChange={onPageEmployeeChange}
                                        pagination={{
                                            ...pagination,
                                            showTotal: (total, range) =>
                                                t("paginate.description")
                                                    .replace("{min}", range[0])
                                                    .replace("{max}", range[1])
                                                    .replace("{total}", total),
                                        }}
                                    />
                                    <Flex gap={8} justify="end">
                                        <Button
                                            type="default"
                                            style={{ borderRadius: "8px", height: "40px", color: "#565656", fontWeight: "bold" }}
                                            onClick={handleCancel}
                                        >
                                            {t("common.cancel")}
                                        </Button>

                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            style={{ borderRadius: "8px", height: "40px", backgroundColor: "var(--button-color", fontWeight: "bold" }}
                                        >
                                            {t("common.save")}
                                        </Button>
                                    </Flex>
                                </Space>
                            )
                        }}

                    </FormItem>

                </FormAntd>
            </Space>
        </Modal>
    )
}

export default WelfareAddEmployeeModal;